/**
 * Sign-up hardening rules — step 1 of the supplier registration flow.
 *
 * Everything here is a *client-side* rule: it gives the supplier an answer while they
 * type instead of after a round trip. None of it is a control on its own. The backend
 * re-checks every one of these, because anything decided in a browser can be skipped.
 */

/** Spec: passwords of at least 8 characters. */
export const PASSWORD_MIN_LENGTH = 8

/**
 * zxcvbn score, 0-4. Raised from the old threshold of 2: at 2 a password survives a
 * few million guesses, which is no longer a meaningful barrier for an account that can
 * accept purchase orders.
 */
export const PASSWORD_MIN_STRENGTH_SCORE = 3

/**
 * Consumer mailbox providers. The spec does not merely discourage these — a supplier
 * signing up from one is not registered at all — so the field blocks rather than warns.
 *
 * A short list on purpose: it catches the overwhelming majority of attempts and stays
 * readable. The authoritative list lives on the server.
 */
export const FREE_MAIL_DOMAINS = [
  'aol.com',
  'gmail.com',
  'gmx.com',
  'gmx.net',
  'hotmail.com',
  'hotmail.co.uk',
  'icloud.com',
  'live.com',
  'mail.com',
  'mail.ru',
  'me.com',
  'msn.com',
  'outlook.com',
  'pm.me',
  'proton.me',
  'protonmail.com',
  'yahoo.com',
  'yandex.com',
  'yandex.ru',
  'zoho.com'
]

/**
 * Throwaway mailbox services. Same reasoning as above — a sample, not the whole list,
 * which changes far too often to keep in the bundle.
 */
export const DISPOSABLE_MAIL_DOMAINS = [
  '10minutemail.com',
  'dispostable.com',
  'fakeinbox.com',
  'getnada.com',
  'guerrillamail.com',
  'mailinator.com',
  'maildrop.cc',
  'mintemail.com',
  'sharklasers.com',
  'temp-mail.org',
  'tempmail.com',
  'throwawaymail.com',
  'trashmail.com',
  'yopmail.com'
]

/**
 * Name of the hidden field bots are meant to fill in.
 *
 * Deliberately plausible: a field called "honeypot" is skipped by anything written in
 * the last decade, whereas an autofill-shaped name gets completed by the naive scripts
 * this is aimed at. It must stay something no real form would ask for twice.
 */
export const HONEYPOT_FIELD_NAME = 'companyWebsiteUrl'

/**
 * A human cannot read this form, pick a password and submit inside three seconds.
 * Anything faster is a script replaying the fields.
 */
export const MIN_FORM_FILL_MS = 3000

/**
 * Phone country used when the browser does not report a region we can map. Most
 * suppliers signing up today are in Türkiye; anyone else picks theirs from the list.
 */
export const DEFAULT_PHONE_COUNTRY = 'TR'
